import { ref, computed, watch } from 'vue'
import { normalizeRole, canViewWidget } from '../utils/roles.js'

/**
 * Composable for user role and widget visibility.
 * Role is resolved from organization.admins via GetMyAdminRole.
 */
export function useUserRole(meDataRef = null, adminRoleDataRef = null) {
  const roleFromApi = ref(null)

  // Derive role from organization.admins by matching current user's user_id
  if (meDataRef && adminRoleDataRef) {
    watch(
      [meDataRef, adminRoleDataRef],
      ([meData, adminData]) => {
        const userId = meData?.me?.user?.user_id
        const admins = adminData?.organization?.admins
        if (!userId || !Array.isArray(admins)) {
          roleFromApi.value = null
          return
        }
        const myAdmin = admins.find((a) => a.user?.user_id === userId)
        if (!myAdmin?.admin_role) {
          roleFromApi.value = null
          return
        }
        roleFromApi.value = normalizeRole(myAdmin.admin_role)
      },
      { immediate: true }
    )
  }

  // URL override for testing: ?role=OWNER, ?role=RECEPTIONIST, etc.
  const urlRoleOverride = typeof window !== 'undefined'
    ? normalizeRole(new URLSearchParams(window.location.search).get('role'))
    : null

  const userRole = computed(() => urlRoleOverride || roleFromApi.value)

  const canView = (widgetMinRole) => canViewWidget(userRole.value, widgetMinRole)

  const filterWidgetsByRole = (widgets) => {
    if (!widgets || !Array.isArray(widgets)) return []
    const role = userRole.value
    if (!role) return widgets.filter((w) => !w.minRole) // No role = only show widgets with no minRole
    return widgets.filter((w) => canViewWidget(role, w.minRole))
  }

  return {
    userRole,
    canView,
    filterWidgetsByRole
  }
}
